import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'wouter';
import { useAuth } from '@clerk/react';
import {
  useGetMarket,
  useCreateAnalysis,
  useGetAnalysis,
  useGetMarketHistory,
  useListPositions,
  useCreatePosition,
  useUpdatePosition,
  getGetAnalysisQueryKey,
} from '@workspace/api-client-react';
import type { Position } from '@workspace/api-client-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlatformBadge } from '@/components/platform-badge';
import { RecommendationBadge } from '@/components/recommendation-badge';
import { RiskBadge } from '@/components/risk-badge';
import { ConfidenceBar } from '@/components/ui/confidence-bar';
import { Spinner } from '@/components/ui/spinner';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  ArrowLeft, Sparkles, ChevronDown, AlertTriangle, Target,
  Clock, Zap, RefreshCw, TrendingUp, Pencil, Plus, XCircle,
  Bookmark, BookmarkCheck, Share2,
} from 'lucide-react';
import { AnalysisChat } from '@/components/analysis-chat';
import { useWatchlist } from '@/hooks/use-watchlist';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useAnalysisNotifications } from '@/hooks/use-analysis-notifications';
import { useToast } from '@/hooks/use-toast';
import { useOgMeta, buildOgImageUrl } from '@/hooks/use-og-meta';
import { trackEvent, FUNNEL_EVENTS } from '@/lib/analytics';
import { getStoredUtmParams } from '@/hooks/use-utm';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

// ─── Price History Chart ────────────────────────────────────────────────────

function PriceHistoryChart({
  marketId,
  platform,
}: {
  marketId: string;
  platform: string;
}) {
  const { data, isLoading, isError, refetch } = useGetMarketHistory(marketId, { platform } as any, {
    query: { staleTime: 5 * 60 * 1000 } as any,
  });
  const points = data?.history ?? [];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">30-Day Price History</span>
          </div>
          <div className="h-40 flex items-center justify-center">
            <Spinner size="sm" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError || points.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">30-Day Price History</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {isError ? 'Could not load history.' : 'No history available yet.'}
            {isError && (
              <button
                onClick={() => refetch()}
                className="ml-2 underline text-primary hover:no-underline"
              >
                Retry
              </button>
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  const chartData = points.map((pt: { t: number; p: number }) => ({
    date: new Date(pt.t * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    yes: Math.round(pt.p * 100),
  }));

  const yesValues = chartData.map((d: { date: string; yes: number }) => d.yes);
  const minY = Math.max(0, Math.min(...yesValues) - 5);
  const maxY = Math.min(100, Math.max(...yesValues) + 5);

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">30-Day YES Price</span>
          <span className="ml-auto text-xs text-muted-foreground">{points.length} data points</span>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[minY, maxY]}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v}¢`}
            />
            <Tooltip
              contentStyle={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(v: number) => [`${v}¢`, 'YES']}
            />
            <Line
              type="monotone"
              dataKey="yes"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: 'hsl(var(--primary))' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function MarketDetailPage() {
  const params = useParams();
  const marketId = params.id as string;
  // The markets page encodes the marketId and appends ?platform=...
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const platformHint = searchParams.get('platform') ?? undefined;
  const queryClient = useQueryClient();

  const [analysisId, setAnalysisId] = useState<number | null>(null);
  const [isReasoningOpen, setIsReasoningOpen] = useState(true);
  const [limitReached, setLimitReached] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [editDraft, setEditDraft] = useState({ entryPrice: '', shares: '', notes: '' });
  const [addingPosition, setAddingPosition] = useState(false);
  const [addDraft, setAddDraft] = useState({ side: 'YES' as 'YES' | 'NO', entryPrice: '', shares: '', notes: '' });
  const [closingPosition, setClosingPosition] = useState<Position | null>(null);
  const [closeOutcome, setCloseOutcome] = useState<'correct' | 'incorrect'>('correct');
  const [sharing, setSharing] = useState(false);
  const viewedMarketKeys = useRef(new Set<string>());

  const { registerAnalysis } = useAnalysisNotifications();
  const { toast } = useToast();
  const { getToken } = useAuth();
  const { isWatching, getWatchlistId, addToWatchlist, removeFromWatchlist } = useWatchlist();

  const {
    data: market,
    isLoading: marketLoading,
    isError: marketError,
    error: marketFetchError,
    isFetching: isRefetchingMarket,
    refetch: refetchMarket,
  } = useGetMarket(decodeURIComponent(marketId), {}, {
    query: {
      retry: 1,
      // Keep market prices fresh: treat data as stale after 20 s and
      // automatically background-refetch every 30 s so the P&L shown
      // on the position rows stays up-to-date without a manual refresh.
      staleTime: 20_000,
      refetchInterval: 30_000,
    } as any,
  });

  // True when the API returned 503 because the upstream price fetch timed out.
  // Distinct from a genuine 404 so the UI can show a stale-data notice instead
  // of a hard "market not found" error.
  const isTimeoutError = (marketFetchError as { status?: number } | null)?.status === 503;

  // A ref keeps background price refreshes and other re-renders from duplicating
  // the detail-view event, while still tracking a different market in this view.
  useEffect(() => {
    if (!market) return;
    const key = `${market.platform}:${market.marketId}`;
    if (viewedMarketKeys.current.has(key)) return;
    viewedMarketKeys.current.add(key);
    trackEvent(FUNNEL_EVENTS.MARKET_VIEWED, {
      marketId: market.marketId,
      platform: market.platform,
      source: 'market_detail',
    });
  }, [market?.marketId, market?.platform]);

  const handleDirectUpgrade = async () => {
    setUpgradeLoading(true);
    trackEvent(FUNNEL_EVENTS.CHECKOUT_STARTED, { tier: 'pro', source: 'market_detail_limit' });
    try {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ utm: getStoredUtmParams() ?? undefined }),
      });
      if (!res.ok) return;
      const { url } = await res.json() as { url: string };
      if (url) window.open(url, '_blank');
    } catch {
      // ignore
    } finally {
      setUpgradeLoading(false);
    }
  };

  const createAnalysis = useCreateAnalysis();
  const createPosition = useCreatePosition();
  const updatePosition = useUpdatePosition();

  // Positions that reference this market (open + closed for completeness)
  const { data: openPosData } = useListPositions({ status: 'open', limit: 50 }, {
    query: { enabled: !!market } as any,
  });
  const marketPositions = (openPosData?.positions ?? []).filter(
    (p) => p.marketId === (market?.marketId ?? decodeURIComponent(marketId)),
  );

  const handleEditPosition = (pos: Position) => {
    setEditingPosition(pos);
    setEditDraft({
      entryPrice: pos.entryPrice != null ? String(pos.entryPrice) : '',
      shares: pos.shares != null ? String(pos.shares) : '',
      notes: pos.notes ?? '',
    });
  };

  const handleOpenAdd = () => {
    setAddDraft({
      side: 'YES',
      entryPrice: market ? String(market.yesPrice.toFixed(2)) : '',
      shares: '',
      notes: '',
    });
    setAddingPosition(true);
  };

  const handleSaveAdd = async () => {
    if (!market) return;
    const entryPrice = Number(addDraft.entryPrice);
    const shares = Number(addDraft.shares);
    if (isNaN(entryPrice) || isNaN(shares) || shares <= 0) return;
    await createPosition.mutateAsync({
      data: {
        marketId: market.marketId,
        platform: market.platform as any,
        marketQuestion: market.question,
        side: addDraft.side,
        entryPrice,
        shares,
        notes: addDraft.notes || undefined,
      },
    });
    queryClient.invalidateQueries({ queryKey: ['/api/positions'] });
    queryClient.invalidateQueries({ queryKey: ['/api/portfolio/summary'] });
    setAddingPosition(false);
  };

  const handleClosePosition = async () => {
    if (!closingPosition || !market) return;
    const livePrice = closingPosition.side === 'YES' ? market.yesPrice : 1 - market.yesPrice;
    const entryPrice = Number(closingPosition.entryPrice ?? 0);
    const shares = Number(closingPosition.shares ?? 0);
    const pnl = (livePrice - entryPrice) * shares;
    try {
      await updatePosition.mutateAsync({
        id: closingPosition.id,
        data: {
          status: 'closed' as any,
          outcome: closeOutcome,
          currentPrice: livePrice,
          pnl,
          closedAt: new Date().toISOString(),
        },
      });
      queryClient.invalidateQueries({ queryKey: ['/api/positions'] });
      queryClient.invalidateQueries({ queryKey: ['/api/portfolio/summary'] });
      setClosingPosition(null);
    } catch {
      toast({
        title: 'Failed to close position',
        description: 'The request failed. Your position is still open — please try again.',
        variant: 'destructive',
      });
      // Dialog stays open; position row is not removed (no invalidation).
    }
  };

  const handleSaveEdit = async () => {
    if (!editingPosition) return;
    const entryPrice = editDraft.entryPrice ? Number(editDraft.entryPrice) : undefined;
    const shares = editDraft.shares ? Number(editDraft.shares) : undefined;
    if (entryPrice !== undefined && isNaN(entryPrice)) return;
    if (shares !== undefined && isNaN(shares)) return;
    await updatePosition.mutateAsync({
      id: editingPosition.id,
      data: { entryPrice, shares, notes: editDraft.notes || undefined },
    });
    queryClient.invalidateQueries({ queryKey: ['/api/positions'] });
    queryClient.invalidateQueries({ queryKey: ['/api/portfolio/summary'] });
    setEditingPosition(null);
  };

  const { data: analysis } = useGetAnalysis(analysisId ?? 0, {
    query: {
      enabled: !!analysisId,
      refetchInterval: (query: any) => (query.state?.data?.status === 'pending' ? 2000 : false),
    } as any,
  });

  // Refresh subscription counter once analysis settles
  useEffect(() => {
    if (analysis?.status === 'complete' || analysis?.status === 'failed') {
      queryClient.invalidateQueries({ queryKey: ['/api/subscription'] });
    }
  }, [analysis?.status, queryClient]);

  // ─── Dynamic OG meta tags ────────────────────────────────────────────────
  const completedAnalysis = analysis?.status === 'complete' ? analysis : null;
  useOgMeta({
    title: market?.question,
    description: completedAnalysis?.edgeSummary
      ?? (market ? `Live prediction market on ${market.platform}. YES: ${Math.round(market.yesPrice * 100)}¢` : undefined),
    imageUrl: market
      ? buildOgImageUrl({
          title: market.question,
          probability: market.yesPrice,
          platform: market.platform,
          summary: completedAnalysis?.edgeSummary ?? undefined,
          recommendation: completedAnalysis?.recommendation ?? undefined,
        })
      : undefined,
  });

  const watching = market ? isWatching(market.marketId, market.platform) : false;

  const handleWatchlistToggle = async () => {
    if (!market) return;
    const wid = getWatchlistId(market.marketId, market.platform);
    try {
      if (wid) {
        await removeFromWatchlist(wid);
        trackEvent(FUNNEL_EVENTS.WATCHLIST_REMOVED, {
          marketId: market.marketId,
          platform: market.platform,
          source: 'market_detail',
        });
        toast({ title: 'Removed from watchlist' });
      } else {
        await addToWatchlist({ marketId: market.marketId, platform: market.platform, marketQuestion: market.question });
        trackEvent(FUNNEL_EVENTS.WATCHLIST_ADDED, {
          marketId: market.marketId,
          platform: market.platform,
          source: 'market_detail',
        });
        toast({ title: 'Saved to watchlist' });
      }
    } catch {
      toast({ title: 'Failed to update watchlist', variant: 'destructive' });
    }
  };

  const handleShare = async () => {
    if (!analysis || analysis.status !== 'complete') return;
    setSharing(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/analyses/${analysis.id}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error('Failed');
      const { shareUrl } = await res.json() as { shareUrl: string; shareToken: string };
      await navigator.clipboard.writeText(shareUrl).catch(() => {});
      toast({ title: 'Link copied!', description: 'Share this analysis with anyone.' });
    } catch {
      toast({ title: 'Failed to generate share link', variant: 'destructive' });
    } finally {
      setSharing(false);
    }
  };

  const handleAnalyze = () => {
    if (!market) return;
    setLimitReached(false);

    createAnalysis.mutate(
      {
        data: {
          marketId: market.marketId,
          platform: market.platform,
          marketQuestion: market.question,
          yesPrice: market.yesPrice,
          noPrice: 1 - market.yesPrice,
          volume: market.volume || 0,
          liquidity: market.liquidity,
          closeDate: market.closeDate,
          category: market.category,
        },
      },
      {
        onSuccess: (data) => {
          setAnalysisId(data.id);
          if (market) registerAnalysis(data.id, market.marketId, market.question);
          trackEvent(FUNNEL_EVENTS.ANALYSIS_RUN, {
            platform: market?.platform,
            marketId: market?.marketId,
          });
        },
        onError: (err: any) => {
          if (err?.status === 403 && err?.data?.code === 'LIMIT_REACHED') {
            setLimitReached(true);
          }
        },
      }
    );
  };

  if (marketLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  // No market data — show error or timeout screen
  if (!market) {
    const heading = isTimeoutError
      ? 'Live prices couldn\'t be loaded in time'
      : marketError
      ? 'Could not load market'
      : 'Market not found';

    const body = isTimeoutError
      ? 'Prices may be up to 60 seconds old. Check your connection and try again.'
      : marketError
      ? 'The market data failed to load. Check your connection and try again.'
      : 'This market may have been removed or the link is invalid.';

    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Link href="/markets" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Markets
        </Link>
        <Card className={isTimeoutError ? 'border-amber-200 bg-amber-50' : 'border-destructive/20 bg-destructive/5'}>
          <CardContent className="p-12 text-center">
            <AlertTriangle className={`h-12 w-12 mx-auto mb-4 ${isTimeoutError ? 'text-amber-500' : 'text-destructive'}`} />
            <h3 className="font-semibold text-lg mb-2">{heading}</h3>
            <p className="text-sm text-muted-foreground mb-6">{body}</p>
            <div className="flex gap-3 justify-center">
              {(marketError || isTimeoutError) && (
                <Button onClick={() => refetchMarket()}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              )}
              <Link href="/markets">
                <Button variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Markets
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const yesPercent = Math.round(market.yesPrice * 100);
  const noPercent = 100 - yesPercent;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6 pb-24 lg:pb-8">
      {/* Back Link */}
      <Link href="/markets" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Markets
      </Link>

      {/* Stale-data banner — background refresh failed but cached data is still shown */}
      {marketError && market && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1">
            {isTimeoutError
              ? 'Showing cached data — prices may be up to 60 seconds old.'
              : 'Showing cached data — live prices could not be fetched.'}
          </span>
          <button
            onClick={() => refetchMarket()}
            disabled={isRefetchingMarket}
            className="font-semibold underline underline-offset-2 hover:no-underline disabled:opacity-50"
          >
            {isRefetchingMarket ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}

      {/* Market Header */}
      <div>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <PlatformBadge platform={market.platform} />
            {market.category && (
              <span className="text-xs px-2 py-1 rounded-md bg-muted text-muted-foreground">
                {market.category}
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant={watching ? 'default' : 'outline'}
            onClick={handleWatchlistToggle}
            title={watching ? 'Remove from watchlist' : 'Save to watchlist'}
            className="shrink-0"
          >
            {watching
              ? <><BookmarkCheck className="h-4 w-4 mr-1.5" />Saved</>
              : <><Bookmark className="h-4 w-4 mr-1.5" />Save</>
            }
          </Button>
        </div>
        <h1 className="text-3xl font-bold mb-4">{market.question}</h1>
        {market.closeDate && (
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <Clock className="h-4 w-4" />
            Closes {new Date(market.closeDate).toLocaleString()}
          </p>
        )}
      </div>

      {/* Price Display */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-600 mb-2">{yesPercent}¢</div>
              <div className="text-sm text-muted-foreground">YES</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-red-600 mb-2">{noPercent}¢</div>
              <div className="text-sm text-muted-foreground">NO</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Volume:</span>
              <span className="ml-2 font-semibold">${market.volume?.toLocaleString() || '0'}</span>
            </div>
            {market.liquidity && (
              <div>
                <span className="text-muted-foreground">Liquidity:</span>
                <span className="ml-2 font-semibold">${market.liquidity.toLocaleString()}</span>
              </div>
            )}
          </div>

          {market.lastUpdated && (
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Updated {new Date(market.lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => refetchMarket()}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Refresh
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Price History Chart */}
      <PriceHistoryChart marketId={market.marketId} platform={platformHint ?? market.platform} />

      {/* Your Positions — add and edit from context */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Your Positions
            </CardTitle>
            <Button size="sm" variant="outline" onClick={handleOpenAdd}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add Position
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {marketPositions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2">
              No open positions for this market yet.
            </p>
          ) : (
            marketPositions.map((pos) => {
              const livePrice = pos.side === 'YES' ? market.yesPrice : 1 - market.yesPrice;
              const entryPrice = Number(pos.entryPrice ?? 0);
              const shares = Number(pos.shares ?? 0);
              const pnl = pos.entryPrice != null && pos.shares != null
                ? (livePrice - entryPrice) * shares
                : null;
              return (
                <div key={pos.id} className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={cn('text-xs font-bold px-2 py-1 rounded', pos.side === 'YES' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800')}>
                      {pos.side}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {pos.entryPrice != null && (
                          <span className="text-sm font-semibold">{Math.round(Number(pos.entryPrice) * 100)}¢ entry</span>
                        )}
                        {pos.shares != null && (
                          <span className="text-sm text-muted-foreground">× {pos.shares} shares</span>
                        )}
                        {pnl !== null && (
                          <span className={cn('text-xs font-semibold', pnl >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                            Est. P&amp;L: {pnl >= 0 ? '+' : '-'}${Math.abs(pnl).toFixed(2)}
                          </span>
                        )}
                      </div>
                      {pos.notes && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{pos.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEditPosition(pos)} title="Edit position">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => { setClosingPosition(pos); setCloseOutcome('correct'); }}
                      title="Close position"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Add Position Dialog */}
      <Dialog open={addingPosition} onOpenChange={(open) => !open && setAddingPosition(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Position</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Side</Label>
                <div className="flex rounded-md overflow-hidden border border-input">
                  {(['YES', 'NO'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setAddDraft((d) => ({
                          ...d,
                          side: s,
                          entryPrice: market ? String(s === 'YES' ? market.yesPrice.toFixed(2) : (1 - market.yesPrice).toFixed(2)) : d.entryPrice,
                        }));
                      }}
                      className={cn(
                        'flex-1 py-2 text-sm font-semibold transition-colors',
                        addDraft.side === s
                          ? s === 'YES' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
                          : 'bg-background text-muted-foreground hover:bg-muted',
                      )}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Entry price (0–1)</Label>
                <Input
                  type="number"
                  min={0}
                  max={1}
                  step={0.01}
                  value={addDraft.entryPrice}
                  onChange={(e) => setAddDraft((d) => ({ ...d, entryPrice: e.target.value }))}
                  placeholder="0.65"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Shares / contracts</Label>
              <Input
                type="number"
                min={1}
                value={addDraft.shares}
                onChange={(e) => setAddDraft((d) => ({ ...d, shares: e.target.value }))}
                placeholder="100"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea
                value={addDraft.notes}
                onChange={(e) => setAddDraft((d) => ({ ...d, notes: e.target.value }))}
                placeholder="Why you entered this position…"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddingPosition(false)}>Cancel</Button>
            <Button onClick={handleSaveAdd} disabled={createPosition.isPending || !addDraft.shares}>
              {createPosition.isPending ? <><Spinner size="sm" className="mr-2" />Saving…</> : 'Add Position'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Position Dialog */}
      <Dialog open={!!editingPosition} onOpenChange={(open) => !open && setEditingPosition(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Position</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Entry price (0–1)</Label>
                <Input
                  type="number"
                  min={0}
                  max={1}
                  step={0.01}
                  value={editDraft.entryPrice}
                  onChange={(e) => setEditDraft((d) => ({ ...d, entryPrice: e.target.value }))}
                  placeholder="0.65"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Shares / contracts</Label>
                <Input
                  type="number"
                  min={0}
                  value={editDraft.shares}
                  onChange={(e) => setEditDraft((d) => ({ ...d, shares: e.target.value }))}
                  placeholder="100"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea
                value={editDraft.notes}
                onChange={(e) => setEditDraft((d) => ({ ...d, notes: e.target.value }))}
                placeholder="Why you entered this position…"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPosition(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={updatePosition.isPending}>
              {updatePosition.isPending ? <><Spinner size="sm" className="mr-2" />Saving…</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Position Dialog */}
      <Dialog open={!!closingPosition} onOpenChange={(open) => !open && setClosingPosition(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Position</DialogTitle>
          </DialogHeader>
          {closingPosition && market && (() => {
            const livePrice = closingPosition.side === 'YES' ? market.yesPrice : 1 - market.yesPrice;
            const entryPrice = Number(closingPosition.entryPrice ?? 0);
            const shares = Number(closingPosition.shares ?? 0);
            const pnl = closingPosition.entryPrice != null && closingPosition.shares != null
              ? (livePrice - entryPrice) * shares
              : null;
            return (
              <div className="space-y-4 pt-2">
                <div className="rounded-lg bg-muted p-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Side</span>
                    <span className="font-semibold">{closingPosition.side}</span>
                  </div>
                  {closingPosition.entryPrice != null && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Entry</span>
                      <span className="font-semibold">{Math.round(Number(closingPosition.entryPrice) * 100)}¢</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Current price</span>
                    <span className="font-semibold">{Math.round(livePrice * 100)}¢</span>
                  </div>
                  {pnl !== null && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Est. P&L</span>
                      <span className={cn('font-bold', pnl >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                        {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Outcome</Label>
                  <div className="flex rounded-md overflow-hidden border border-input">
                    {(['correct', 'incorrect'] as const).map((o) => (
                      <button
                        key={o}
                        type="button"
                        onClick={() => setCloseOutcome(o)}
                        className={cn(
                          'flex-1 py-2 text-sm font-semibold capitalize transition-colors',
                          closeOutcome === o
                            ? o === 'correct' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
                            : 'bg-background text-muted-foreground hover:bg-muted',
                        )}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosingPosition(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleClosePosition}
              disabled={updatePosition.isPending}
            >
              {updatePosition.isPending ? <><Spinner size="sm" className="mr-2" />Closing…</> : 'Close Position'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analysis Section */}
      {limitReached ? (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-amber-100 mx-auto mb-4 flex items-center justify-center">
              <Zap className="h-8 w-8 text-amber-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2 text-amber-900">Monthly Analysis Limit Reached</h3>
            <p className="text-sm text-amber-800 mb-6 max-w-sm mx-auto">
              You've used all 5 free analyses this month. Upgrade to Pro for unlimited AI analyses.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                className="w-full sm:w-auto"
                onClick={handleDirectUpgrade}
                disabled={upgradeLoading}
              >
                {upgradeLoading ? (
                  <><Spinner size="sm" className="mr-2" />Opening Checkout…</>
                ) : (
                  <><Zap className="mr-2 h-4 w-4" />Upgrade to Pro</>
                )}
              </Button>
              <Link href="/markets">
                <Button variant="outline" size="lg" className="w-full sm:w-auto">Back to Markets</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : !analysisId ? (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-primary/10 mx-auto mb-4 flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Get AI Analysis</h3>
            <p className="text-muted-foreground mb-6">Let Claude analyze this market and find potential edges</p>
            <Button size="lg" onClick={handleAnalyze} disabled={createAnalysis.isPending}>
              {createAnalysis.isPending ? (
                <><Spinner size="sm" className="mr-2" />Starting Analysis...</>
              ) : (
                <><Sparkles className="mr-2 h-5 w-5" />Run AI Analysis</>
              )}
            </Button>
          </CardContent>
        </Card>
      ) : analysis?.status === 'pending' ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Spinner size="lg" className="mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">Claude is analyzing this market…</h3>
            <p className="text-sm text-muted-foreground">This usually takes 10–30 seconds</p>
          </CardContent>
        </Card>
      ) : analysis?.status === 'failed' ? (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">Analysis Failed</h3>
            <p className="text-sm text-muted-foreground mb-6">
              {(analysis as any).error || 'Something went wrong. Please try again.'}
            </p>
            <Button onClick={handleAnalyze} disabled={createAnalysis.isPending}>Try Again</Button>
          </CardContent>
        </Card>
      ) : analysis?.status === 'complete' && analysis.recommendation ? (
        <div className="space-y-6">
          <Card className="border-2 border-primary/20">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle>AI Analysis</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleShare}
                    disabled={sharing}
                    title="Copy share link"
                  >
                    {sharing ? <Spinner size="sm" /> : <Share2 className="h-4 w-4" />}
                    <span className="ml-1.5 text-xs">Share</span>
                  </Button>
                  <RecommendationBadge recommendation={analysis.recommendation as 'YES' | 'NO'} size="lg" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div><ConfidenceBar confidence={analysis.confidence || 0} /></div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Risk Level</span>
                    <RiskBadge risk={analysis.riskLevel as any} />
                  </div>
                </div>
              </div>

              {analysis.edgeSummary && (
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Edge Summary
                  </h4>
                  <p className="text-sm">{analysis.edgeSummary}</p>
                </div>
              )}

              {analysis.reasoning && (
                <Collapsible open={isReasoningOpen} onOpenChange={setIsReasoningOpen}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-4 rounded-lg hover:bg-muted transition-colors">
                    <h4 className="font-semibold">Full Reasoning</h4>
                    <ChevronDown className={cn('h-5 w-5 transition-transform', isReasoningOpen && 'rotate-180')} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <p className="text-sm whitespace-pre-wrap">{analysis.reasoning}</p>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {(analysis.targetPrice || (analysis as any).stopLoss || (analysis as any).timeHorizon) && (
                <div>
                  <h4 className="font-semibold mb-3">Game Plan</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    {analysis.targetPrice && (
                      <div className="p-4 rounded-lg border border-border">
                        <div className="text-xs text-muted-foreground mb-1">Cash out at</div>
                        <div className="text-xl font-bold text-emerald-600">{Math.round(analysis.targetPrice * 100)}¢</div>
                      </div>
                    )}
                    {(analysis as any).stopLoss && (
                      <div className="p-4 rounded-lg border border-border">
                        <div className="text-xs text-muted-foreground mb-1">Bail at</div>
                        <div className="text-xl font-bold text-red-600">{Math.round((analysis as any).stopLoss * 100)}¢</div>
                      </div>
                    )}
                    {(analysis as any).timeHorizon && (
                      <div className="p-4 rounded-lg border border-border">
                        <div className="text-xs text-muted-foreground mb-1">Hold ~</div>
                        <div className="text-xl font-bold">{(analysis as any).timeHorizon}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-amber-900">⚠️ Not financial advice. Educational purposes only. Always do your own research.</p>
              </div>
            </CardContent>
          </Card>

          {/* AI Follow-up Chat */}
          <AnalysisChat
            analysisId={analysis.id}
            marketId={market.marketId}
            platform={market.platform}
            marketQuestion={market.question}
          />
        </div>
      ) : null}
    </div>
  );
}
